import { StringField } from "@opendaw/lib-box";
import { Option, Terminable } from "@opendaw/lib-std";
import { Pointers } from "@opendaw/studio-enums";
import { AudioUnitBox, ModulationBox } from "@opendaw/studio-boxes";
import { AudioUnitBoxAdapter } from "./audio-unit/AudioUnitBoxAdapter";
import { ModulationBoxAdapter } from "./modulation/ModulationBoxAdapter";
import { Devices } from "./DeviceAdapter";
export var ParameterOwner;
(function (ParameterOwner) {
    /// A scriptable device's dynamic parameter lives in its own child box, so the walk falls back to the
    /// device that owns it.
    ParameterOwner.nameOf = (context, vertex) => {
        const box = vertex.box;
        if (box instanceof AudioUnitBox) {
            return context.boxAdapters.adapterFor(box, AudioUnitBoxAdapter).input.label;
        }
        if (box instanceof ModulationBox) {
            const adapter = context.boxAdapters.adapterFor(box, ModulationBoxAdapter);
            return Option.wrap(`${adapter.source.label} \u2192 ${adapter.targetOwner.unwrapOrElse("")} ${adapter.target.mapOr(parameter => parameter.name, "")}`.trim());
        }
        const own = labelOf(context, box);
        if (own.nonEmpty()) {
            return own;
        }
        const owner = resolveOwnerDeviceBox(box).flatMap(owner => labelOf(context, owner));
        return owner.nonEmpty() ? owner : Option.wrap(box.name);
    };
    /// The same name as `nameOf`, but following the rename: a view stays in sync when the device (or the
    /// audio unit's instrument) is relabelled.
    ParameterOwner.catchupAndSubscribeName = (context, vertex, observer) => {
        const box = vertex.box;
        if (box instanceof AudioUnitBox) {
            return context.boxAdapters.adapterFor(box, AudioUnitBoxAdapter).input
                .catchupAndSubscribeLabelChange(label => observer(label.unwrapOrElse("")));
        }
        const own = labelFieldOf(context, box);
        const field = own.nonEmpty()
            ? own
            : resolveOwnerDeviceBox(box).flatMap(owner => labelFieldOf(context, owner));
        if (field.isEmpty()) {
            observer(ParameterOwner.nameOf(context, vertex).unwrapOrElse(box.name));
            return Terminable.Empty;
        }
        return field.unwrap().catchupAndSubscribe(field => observer(field.getValue()));
    };
    ParameterOwner.audioUnitOf = (context, vertex) => {
        const box = vertex.box;
        if (box instanceof AudioUnitBox) {
            return Option.wrap(context.boxAdapters.adapterFor(box, AudioUnitBoxAdapter));
        }
        // A modulated DEPTH owns no device of its own, so the walk follows the assignment to what it drives.
        if (box instanceof ModulationBox) {
            return box.target.targetVertex.flatMap(target => ParameterOwner.audioUnitOf(context, target));
        }
        const own = unitOf(context, box);
        if (own.nonEmpty()) {
            return own;
        }
        return resolveOwnerDeviceBox(box).flatMap(owner => unitOf(context, owner));
    };
    const unitOf = (context, box) => context.boxAdapters.optAdapter(box)
        .flatMap(adapter => Devices.isAny(adapter)
        ? Option.wrap(adapter.audioUnitBoxAdapter())
        : Option.None);
    const labelOf = (context, box) => labelFieldOf(context, box).map(field => field.getValue());
    const labelFieldOf = (context, box) => context.boxAdapters.optAdapter(box).flatMap(adapter => "labelField" in adapter && adapter.labelField instanceof StringField
        ? Option.wrap(adapter.labelField)
        : Option.None);
    const resolveOwnerDeviceBox = (box) => {
        for (const [pointer] of box.outgoingEdges()) {
            if (pointer.pointerType === Pointers.Parameter) {
                return pointer.targetVertex.map(vertex => vertex.box);
            }
        }
        return Option.None;
    };
})(ParameterOwner || (ParameterOwner = {}));
